import { getTunables } from '../config/tunables';

export type BossType =
  | 'rift-core'
  | 'glutton-slime'
  | 'crystal-titan'
  | 'hive-heart'
  | 'pendulum-knight'
  | 'shadow-weaver'
  | 'iron-bastion'
  | 'storm-eye'
  | 'blood-furnace'
  | 'mirror-shade'
  | 'training-dummy';

export type BossBehavior =
  | 'rift-core'
  | 'glutton-slime'
  | 'crystal-titan'
  | 'hive-heart'
  | 'pendulum-knight'
  | 'shadow-weaver'
  | 'iron-bastion'
  | 'storm-eye'
  | 'blood-furnace'
  | 'mirror-shade'
  | 'training-dummy';

export interface BossDefinition {
  type: BossType;
  label: string;
  skillSummary: string;
  behavior: BossBehavior;
  hp: number;
  radius: number;
  phaseTwoRatio: number;
  phaseOneSpeed: number;
  phaseTwoSpeed: number;
  collisionDamage: number;
  color: number;
  phaseTwoColor: number;
  stroke: number;
}

const BASE_BOSS_DEFINITIONS: Record<BossType, BossDefinition> = {
  'rift-core': {
    type: 'rift-core',
    label: '裂隙核心',
    skillSummary: '追击 · 冲击波 · 召唤',
    behavior: 'rift-core',
    hp: 200,
    radius: 30,
    phaseTwoRatio: 0.5,
    phaseOneSpeed: 100,
    phaseTwoSpeed: 150,
    collisionDamage: 20,
    color: 0xb73535,
    phaseTwoColor: 0xff7f3f,
    stroke: 0xffc1a1,
  },
  'glutton-slime': {
    type: 'glutton-slime',
    label: '贪食史莱姆',
    skillSummary: '吞噬材料回血',
    behavior: 'glutton-slime',
    hp: 150,
    radius: 27,
    phaseTwoRatio: 0.45,
    phaseOneSpeed: 75,
    phaseTwoSpeed: 105,
    collisionDamage: 14,
    color: 0x62b85c,
    phaseTwoColor: 0x8ee35d,
    stroke: 0xd5ffc6,
  },
  'crystal-titan': {
    type: 'crystal-titan',
    label: '晶刺巨兽',
    skillSummary: '地刺阵列',
    behavior: 'crystal-titan',
    hp: 180,
    radius: 30,
    phaseTwoRatio: 0.4,
    phaseOneSpeed: 55,
    phaseTwoSpeed: 70,
    collisionDamage: 18,
    color: 0x62cfff,
    phaseTwoColor: 0x8a6cff,
    stroke: 0xd8fbff,
  },
  'hive-heart': {
    type: 'hive-heart',
    label: '巢穴之心',
    skillSummary: '刷兵 · 护盾',
    behavior: 'hive-heart',
    hp: 160,
    radius: 32,
    phaseTwoRatio: 0.5,
    phaseOneSpeed: 0,
    phaseTwoSpeed: 0,
    collisionDamage: 12,
    color: 0x55b66c,
    phaseTwoColor: 0x98e05c,
    stroke: 0xd8ffd9,
  },
  'pendulum-knight': {
    type: 'pendulum-knight',
    label: '钟摆骑士',
    skillSummary: '近远免疫切换',
    behavior: 'pendulum-knight',
    hp: 170,
    radius: 28,
    phaseTwoRatio: 0.5,
    phaseOneSpeed: 90,
    phaseTwoSpeed: 125,
    collisionDamage: 18,
    color: 0xc6a45d,
    phaseTwoColor: 0xf0d76d,
    stroke: 0xffefb8,
  },
  'shadow-weaver': {
    type: 'shadow-weaver',
    label: '影织者',
    skillSummary: '传送 · 毒池',
    behavior: 'shadow-weaver',
    hp: 145,
    radius: 25,
    phaseTwoRatio: 0.4,
    phaseOneSpeed: 60,
    phaseTwoSpeed: 82,
    collisionDamage: 12,
    color: 0x7e5bcb,
    phaseTwoColor: 0xbd6cff,
    stroke: 0xe6d4ff,
  },
  'iron-bastion': {
    type: 'iron-bastion',
    label: '铁壁',
    skillSummary: '护盾 · 冲撞',
    behavior: 'iron-bastion',
    hp: 220,
    radius: 31,
    phaseTwoRatio: 0.5,
    phaseOneSpeed: 54,
    phaseTwoSpeed: 72,
    collisionDamage: 22,
    color: 0x5b84a8,
    phaseTwoColor: 0x8ab0c9,
    stroke: 0xd8edf7,
  },
  'storm-eye': {
    type: 'storm-eye',
    label: '风暴之眼',
    skillSummary: '弹幕 · 闪电',
    behavior: 'storm-eye',
    hp: 150,
    radius: 25,
    phaseTwoRatio: 0.45,
    phaseOneSpeed: 80,
    phaseTwoSpeed: 105,
    collisionDamage: 14,
    color: 0xd9c449,
    phaseTwoColor: 0xffe76d,
    stroke: 0xfff6bd,
  },
  'blood-furnace': {
    type: 'blood-furnace',
    label: '血焰熔炉',
    skillSummary: '越战越强 · 自爆',
    behavior: 'blood-furnace',
    hp: 190,
    radius: 29,
    phaseTwoRatio: 0.3,
    phaseOneSpeed: 60,
    phaseTwoSpeed: 120,
    collisionDamage: 18,
    color: 0xc54a2f,
    phaseTwoColor: 0xff7d2e,
    stroke: 0xffd4a6,
  },
  'mirror-shade': {
    type: 'mirror-shade',
    label: '镜影',
    skillSummary: '幻影分身',
    behavior: 'mirror-shade',
    hp: 155,
    radius: 25,
    phaseTwoRatio: 0.5,
    phaseOneSpeed: 82,
    phaseTwoSpeed: 110,
    collisionDamage: 13,
    color: 0x5ad0d4,
    phaseTwoColor: 0x8cf2ff,
    stroke: 0xdcfbff,
  },
  'training-dummy': {
    type: 'training-dummy',
    label: '训练木桩',
    skillSummary: '无攻击 · DPS 测试',
    behavior: 'training-dummy',
    hp: 100000,
    radius: 26,
    phaseTwoRatio: 0,
    phaseOneSpeed: 0,
    phaseTwoSpeed: 0,
    collisionDamage: 0,
    color: 0xb38a55,
    phaseTwoColor: 0xb38a55,
    stroke: 0xf5dfb8,
  },
};

export const BOSS_TYPES = Object.keys(BASE_BOSS_DEFINITIONS) as BossType[];
const BOSS_TUNABLE_BASE = BASE_BOSS_DEFINITIONS['rift-core'];

export function getBossDefinition(type: BossType): BossDefinition {
  const definition = BASE_BOSS_DEFINITIONS[type];
  if (!definition) {
    throw new Error(`Unknown boss type: ${type}`);
  }

  if (type === 'training-dummy') {
    return { ...definition };
  }

  const boss = getTunables().boss;
  const hpRatio = boss.hp / BOSS_TUNABLE_BASE.hp;
  const radiusRatio = boss.radius / BOSS_TUNABLE_BASE.radius;
  const phaseOneSpeedRatio = boss.phaseOneSpeed / BOSS_TUNABLE_BASE.phaseOneSpeed;
  const phaseTwoSpeedRatio = boss.phaseTwoSpeed / BOSS_TUNABLE_BASE.phaseTwoSpeed;
  const collisionDamageRatio = boss.collisionDamage / BOSS_TUNABLE_BASE.collisionDamage;

  return {
    ...definition,
    hp: Math.max(1, Math.round(definition.hp * hpRatio)),
    radius: Math.max(12, Math.round(definition.radius * radiusRatio)),
    phaseOneSpeed: Math.max(0, Math.round(definition.phaseOneSpeed * phaseOneSpeedRatio)),
    phaseTwoSpeed: Math.max(0, Math.round(definition.phaseTwoSpeed * phaseTwoSpeedRatio)),
    collisionDamage: Math.max(0, Math.round(definition.collisionDamage * collisionDamageRatio)),
  };
}
